"use client"

import React from "react"
import { ChevronLeft, ChevronRight, Download } from "lucide-react"

import { cn } from "@/lib/utils"
import { Badge } from "@/shadcn/components/ui/badge"
import { Button } from "@/shadcn/components/ui/button"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/shadcn/components/ui/pagination"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shadcn/components/ui/table"
// The <FormattedNumber> component can't be used from TypeScript: react-intl 7
// pins @types/react to "16 || 17 || 18" as a hard dependency, so its element
// types don't match the workspace's @types/react 19 (TS2786). formatNumber
// returns a string, keeping React types out of the package boundary.
import { useIntl } from 'react-intl'



// Payment History page. Figma: S311lGXE6JSyTnNAXHmtR7 node 27631:24611.
// Title + subtitle over an invoice table with pagination. Each row has a
// download action. Wiring-free: rows come in via props with defaults from the
// design; the row download and pagination are presentational (wired in prod).
//
// FONT NOTE: the Figma header is set in Geist; rendered here in the theme font
// (Inter) per project rules — flag with the user if Geist is required.
// DEV NOTE: Invoice statuses map to current Recurly API and possible values
type InvoiceStatus = "open" | "pending" | "processing" | "past_due" | "paid" | "closed" | "failed" | "voided"

type Invoice = {
  number: string
  status: InvoiceStatus
  date: string
  amount: number
}

declare var moment: any;

const STATUS: Record<
  InvoiceStatus,
  { label: string; variant: "default" | "destructive" | "outline" }
> = {
  open: { label: "Open", variant: "outline"},
  pending: { label: "Pending", variant: "outline"},
  processing: { label: "Processing", variant: "outline"},
  past_due: { label: "Past Due", variant: "destructive"},
  paid: { label: "Paid", variant: "default" },
  closed: { label: "Closed", variant: "outline" },
  failed: { label: "Failed", variant: "destructive" },
  voided: { label: "Voided", variant: "outline"}
}


const COLUMNS = ["Name", "Status", "Billing date", "Amount"]

// Windowed page list: first + last + current±1, with an ellipsis across gaps.
function getPageItems(current: number, total: number): (number | "ellipsis")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const items: (number | "ellipsis")[] = [1]
  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)
  if (start > 2) items.push("ellipsis")
  for (let i = start; i <= end; i++) items.push(i)
  if (end < total - 1) items.push("ellipsis")
  items.push(total)
  return items
}

function StatusBadge({ status }: { status: InvoiceStatus }) {
  return STATUS[status] ? <Badge variant={STATUS[status].variant}>{STATUS[status].label}</Badge> : null
}

type PaymentHistoryProps = {
  className?: string
  heading?: string
  subheading?: string
  invoices?: Invoice[]
  /** Rows per page for the client-side pagination. */
  pageSize?: number
  /** Show skeleton rows while data loads (state not in Figma). */
  loading?: boolean
  openInvoice?: () => void
}

export default function PaymentHistory({
  className,
  heading = "Payment History",
  subheading = "Manage dlvr.it account settings.",
  invoices = [],
  pageSize = 8,
  loading = false,
  openInvoice = () => {}
}: PaymentHistoryProps) {
  const [page, setPage] = React.useState(1)
  const intl = useIntl()
  const isEmpty = !loading && invoices.length === 0
  const totalPages = Math.max(1, Math.ceil(invoices.length / pageSize))
  // Clamp on read so the view stays valid if `invoices` shrinks.
  const currentPage = Math.min(page, totalPages)
  const pageInvoices = invoices.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  // NOTE: invoice.amount comes from the /subscription/invoices api call and will be formatted properly

  return (
    <div
      className={cn(
        "tw:mx-auto tw:flex tw:w-full tw:max-w-[968px] tw:font-sans tw:flex-col tw:gap-4 tw:text-foreground tw:p-4 tw:md:p-6",
        className
      )}
    >
      <header className="tw:flex tw:flex-col tw:gap-1 tw:py-4">
        <h1 className="tw:text-2xl tw:font-semibold">{heading}</h1>
        <p className="tw:text-sm tw:text-muted-foreground">{subheading}</p>
      </header>

      {/* Desktop: table (md and up) */}
      <div className="tw:hidden tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:md:block">
        <Table className="tw:min-w-[640px]">
          <TableHeader>
            <TableRow>
              {COLUMNS.map((col, i) => (
                <TableHead key={col} className={cn("tw:h-12", i === 0 && "tw:pl-6")}>
                  {col}
                </TableHead>
              ))}
              <TableHead className="tw:h-12 tw:w-0 tw:pr-6">
                <span className="tw:sr-only">Download</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {COLUMNS.map((col, i) => (
                      <TableCell key={col} className={cn("tw:h-16", i === 0 && "tw:pl-6")}>
                        <div className="tw:h-4 tw:w-24 tw:animate-pulse tw:rounded tw:bg-muted" />
                      </TableCell>
                    ))}
                    <TableCell className="tw:h-16 tw:pr-6" />
                  </TableRow>
                ))
              : pageInvoices.map((invoice) => (
                  <TableRow key={invoice.number}>
                    <TableCell className="tw:h-16 tw:pl-6 tw:font-medium">
                      Invoice {invoice.number}
                    </TableCell>
                    <TableCell className="tw:h-16">
                      <StatusBadge status={invoice.status} />
                    </TableCell>
                    <TableCell className="tw:h-16 tw:text-muted-foreground">
                      {moment(invoice.date).format("MMM DD, YYYY h:mm a")}
                    </TableCell>
                    <TableCell className="tw:h-16 tw:text-muted-foreground">
                      {intl.formatNumber(invoice.amount, { style: "currency", currency: "USD" })}
                    </TableCell>
                    <TableCell className="tw:h-16 tw:pr-6 tw:text-right">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Download ${invoice.number}`}
                        onClick={openInvoice}
                        data-invoice={invoice.number}
                      >
                        <Download />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>

        {isEmpty ? (
          <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-1 tw:px-6 tw:py-16 tw:text-center">
            <p className="tw:text-sm tw:font-medium">No payment history yet</p>
            <p className="tw:text-sm tw:text-muted-foreground">
              Your invoices will appear here once you&apos;ve been billed.
            </p>
          </div>
        ) : !loading ? (
          <div className="tw:flex tw:justify-end tw:bg-muted tw:p-4">
            <Pagination className="tw:mx-0 tw:w-auto">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    aria-disabled={currentPage === 1}
                    className={
                      currentPage === 1
                        ? "tw:pointer-events-none tw:opacity-50"
                        : undefined
                    }
                    onClick={(e) => {
                      e.preventDefault()
                      if (currentPage > 1) setPage(currentPage - 1)
                    }}
                  />
                </PaginationItem>
                {getPageItems(currentPage, totalPages).map((item, i) =>
                  item === "ellipsis" ? (
                    <PaginationItem key={`ellipsis-${i}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={item}>
                      <PaginationLink
                        href="#"
                        isActive={item === currentPage}
                        onClick={(e) => {
                          e.preventDefault()
                          setPage(item)
                        }}
                      >
                        {item}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    aria-disabled={currentPage === totalPages}
                    className={
                      currentPage === totalPages
                        ? "tw:pointer-events-none tw:opacity-50"
                        : undefined
                    }
                    onClick={(e) => {
                      e.preventDefault()
                      if (currentPage < totalPages) setPage(currentPage + 1)
                    }}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        ) : null}
      </div>

      {/* Mobile: stacked list of rows (below md). Figma 29079-1895. */}
      <div className="tw:flex tw:flex-col tw:gap-4 tw:md:hidden">
        {isEmpty ? (
          <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-1 tw:rounded-lg tw:border tw:border-border tw:px-6 tw:py-16 tw:text-center">
            <p className="tw:text-sm tw:font-medium">No payment history yet</p>
            <p className="tw:text-sm tw:text-muted-foreground">
              Your invoices will appear here once you&apos;ve been billed.
            </p>
          </div>
        ) : (
          <>
            <div className="tw:flex tw:flex-col tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-card">
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <React.Fragment key={i}>
                      {i > 0 ? <div className="tw:h-px tw:bg-border" /> : null}
                      <div className="tw:flex tw:items-center tw:gap-3 tw:px-4 tw:py-3">
                        <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-1">
                          <div className="tw:h-4 tw:w-24 tw:animate-pulse tw:rounded tw:bg-muted" />
                          <div className="tw:h-3 tw:w-32 tw:animate-pulse tw:rounded tw:bg-muted" />
                        </div>
                      </div>
                    </React.Fragment>
                  ))
                : pageInvoices.map((invoice, i) => (
                    <React.Fragment key={invoice.number}>
                      {i > 0 ? <div className="tw:h-px tw:bg-border" /> : null}
                      <div className="tw:flex tw:items-center tw:gap-3 tw:px-4 tw:py-3">
                        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1">
                          <p className="tw:text-sm tw:font-medium">
                            Invoice {invoice.number}
                          </p>
                          <p className="tw:text-xs tw:text-muted-foreground">
                            {moment(invoice.date).format("MMM DD, YYYY h:mm a")}
                          </p>
                        </div>
                        <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-2">
                          <StatusBadge status={invoice.status} />
                          <button
                            type="button"
                            aria-label={`Download ${invoice.number}`}
                            onClick={openInvoice}
                            data-invoice={invoice.number}
                            className="tw:shrink-0 tw:text-foreground tw:transition-opacity tw:hover:opacity-70"
                          >
                            <Download className="tw:size-5" />
                          </button>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
            </div>

            {!loading ? (
              <div className="tw:flex tw:items-center tw:justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  <ChevronLeft />
                  Previous
                </Button>
                <span className="tw:text-sm tw:text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                  <ChevronRight />
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
